import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Leaf, Shield, ArrowRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SetupPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);

  const checkSetupStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/setup/status`);
      if (!response.data.needs_setup) {
        // Setup already complete, redirect to login
        navigate("/", { replace: true });
      }
    } catch (error) {
      console.error("Setup check error:", error);
    } finally {
      setChecking(false);
    }
  }, [navigate]);

  useEffect(() => {
    checkSetupStatus();
  }, [checkSetupStatus]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/setup/admin`, { email, name });
      toast.success("Admin başarıyla oluşturuldu! Şimdi Google ile giriş yapabilirsiniz.");
      setTimeout(() => navigate("/", { replace: true }), 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Admin oluşturulamadı");
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]">
        <div className="w-8 h-8 border-4 border-[#1A4D2E] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1A4D2E] to-[#2D6A4F] flex items-center justify-center p-6">
      <Card className="w-full max-w-md border-0 shadow-2xl">
        <CardHeader className="text-center pb-2">
          <div className="w-16 h-16 bg-[#1A4D2E] rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Leaf className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl text-[#111827]">EcoAudit Pro Kurulum</CardTitle>
          <CardDescription className="text-[#4B5563]">
            Sistemi kullanmaya başlamak için ilk admin hesabını oluşturun
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="p-4 rounded-lg bg-[#E8F3EE] border border-[#1A4D2E]/20">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-[#1A4D2E] mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-[#1A4D2E]">Önemli</p>
                  <p className="text-[#4B5563] mt-1">
                    Bu e-posta adresiyle Google ile giriş yapabileceksiniz. 
                    Admin olarak diğer kullanıcıları sisteme ekleyebilirsiniz.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="name">Ad Soyad</Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Admin"
                required
                data-testid="setup-name-input"
              />
            </div>

            <div>
              <Label htmlFor="email">Google E-posta Adresi</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@gmail.com"
                required
                data-testid="setup-email-input"
              />
              <p className="text-xs text-[#9CA3AF] mt-1">
                Google hesabınıza ait e-posta adresini girin
              </p>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-[#1A4D2E] hover:bg-[#1A4D2E]/90"
              disabled={loading}
              data-testid="setup-submit-btn"
            >
              {loading ? "Oluşturuluyor..." : "Admin Hesabı Oluştur"}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default SetupPage;
